import React, { useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Globe, ChevronDown, Check } from "lucide-react";
import { useT } from "@/i18n/LanguageContext";
import { useClickOutsideAttr } from "@/hooks/useClickOutsideAttr";

// Desktop language picker (globe icon + code + dropdown of native names).
// Uses `data-lang-menu` attribute + useClickOutsideAttr to auto-close when
// clicking outside the menu.
export default function LangSelector({ open, setOpen }) {
  const { lang, setLang, locales, meta } = useT();

  const close = useCallback(() => setOpen(false), [setOpen]);
  useClickOutsideAttr("data-lang-menu", open, close);

  return (
    <div className="relative ml-2" data-lang-menu>
      <button
        data-testid="lang-selector-button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-medium text-neutral-800 hover:text-[#25bfcf] hover:bg-neutral-100 transition-colors"
        aria-haspopup="true"
        aria-expanded={open}
        aria-label="Select language"
      >
        <Globe className="w-4 h-4" />
        <span className="uppercase text-xs font-semibold">{meta.code}</span>
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.ul
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.18 }}
            className="absolute right-0 top-full mt-1 min-w-[190px] rounded-2xl bg-white shadow-2xl border border-black/5 overflow-hidden py-2"
          >
            {locales.map((l) => (
              <li key={l.code}>
                <button
                  data-testid={`lang-option-${l.code}`}
                  onClick={() => {
                    setLang(l.code);
                    setOpen(false);
                  }}
                  className={`w-full flex items-center gap-3 px-4 py-2 text-sm hover:bg-neutral-50 transition-colors ${
                    l.code === lang ? "text-[#25bfcf] font-semibold" : "text-neutral-800"
                  }`}
                >
                  <span className="text-base">{l.flag}</span>
                  <span className="flex-1 text-left">{l.native}</span>
                  {l.code === lang && <Check className="w-4 h-4" />}
                </button>
              </li>
            ))}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
}
